import { useState } from 'react';
import { extraDevices, getModule, planChangeLimits, planChangeModules, quoteMonthly } from '@onepos/shared';
import { Alert, Badge, Button, Card, DataList, Icon, Modal, SelectField, TextAreaField, TextField, useAction, useAsync } from '@onepos/ui';
import { MonthlyPreview } from '../../components/MonthlyPreview';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { STATUS, fmtDate, fmtDaysLeft, fmtMoney, fmtPhone, limitsText } from '../../lib/format';
import type { Plan, TenantDetail } from '../../lib/types';

interface TabProps {
  data: TenantDetail;
  reload: () => void;
}

export function OverviewTab({ data, reload }: TabProps) {
  const me = useMe();
  const t = data.tenant;
  const [edit, setEdit] = useState(false);
  const [plan, setPlan] = useState(false);
  const [trial, setTrial] = useState(false);

  return (
    <div className="cn-two">
      <Card
        title="Asosiy ma’lumot"
        actions={
          me.role !== 'billing' && (
            <Button size="sm" variant="ghost" onClick={() => setEdit(true)}>
              <Icon name="edit" /> Tahrirlash
            </Button>
          )
        }
      >
        <DataList
          items={[
            { label: 'Nomi', value: t.name },
            { label: 'Shahar', value: t.city ?? '—' },
            { label: 'Ega', value: t.owner_name ?? '—' },
            { label: 'Ega telefoni', value: <span className="op-num">{fmtPhone(t.owner_phone)}</span> },
            { label: 'Shablon', value: `${t.template_key} · v${t.template_version}` },
            { label: 'Filiallar', value: data.branches.map((b) => b.name).join(', ') || '—' },
          ]}
        />
      </Card>

      <Card title="Obuna">
        <DataList
          items={[
            { label: 'Holat', value: <Badge tone={STATUS[t.status].tone}>{STATUS[t.status].label}</Badge> },
            {
              label: 'Tarif',
              value: (
                <>
                  {data.plan.label}
                  {data.plan.monthly_price !== null && (
                    <span className="cn-table__sub">{fmtMoney(data.plan.monthly_price)} / oy</span>
                  )}
                </>
              ),
            },
            {
              label: 'Sinov tugashi',
              value: t.trial_ends_at ? `${fmtDate(t.trial_ends_at)} (${fmtDaysLeft(t.trial_ends_at)})` : '—',
            },
            { label: 'To‘langan muddat', value: fmtDate(t.paid_until) },
            { label: 'Muhlat', value: `${t.grace_days} kun${t.auto_suspend ? ', keyin avtomatik to‘xtatiladi' : ' (avtomatik to‘xtatilmaydi)'}` },
          ]}
        />
        <div className="cn-row" style={{ marginTop: 14 }}>
          {me.role !== 'support' && (
            <Button size="sm" onClick={() => setPlan(true)}>
              <Icon name="card" /> Tarifni almashtirish
            </Button>
          )}
          <Button size="sm" onClick={() => setTrial(true)}>
            <Icon name="clock" /> Sinov muddati
          </Button>
        </div>
      </Card>

      <NoteCard tenantId={t.id} initial={data.note ?? ''} />

      <EditModal key={`e${String(edit)}`} open={edit} onClose={() => setEdit(false)} data={data} onDone={reload} />
      <PlanModal key={`p${String(plan)}`} open={plan} onClose={() => setPlan(false)} data={data} onDone={reload} />
      <TrialModal key={`t${String(trial)}`} open={trial} onClose={() => setTrial(false)} data={data} onDone={reload} />
    </div>
  );
}

function NoteCard({ tenantId, initial }: { tenantId: string; initial: string }) {
  const [note, setNote] = useState(initial);
  const [lastSaved, setLastSaved] = useState(initial);
  const [saved, setSaved] = useState(false);
  const act = useAction(async () => {
    await api.rpc('platform_note_set', { p_tenant: tenantId, p_note: note });
    setLastSaved(note);
    setSaved(true);
  });
  return (
    <Card title="Ichki eslatma" actions={<Badge tone="neutral">restoran ko‘rmaydi</Badge>}>
      <TextAreaField
        label="Eslatma"
        rows={4}
        value={note}
        placeholder="Masalan: ega kechqurun qo‘ng‘iroqni afzal ko‘radi"
        onChange={(e) => {
          setNote(e.target.value);
          setSaved(false);
        }}
      />
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <div className="cn-row" style={{ marginTop: 10 }}>
        <Button size="sm" variant="primary" disabled={act.busy || note === lastSaved} onClick={() => void act.run()}>
          Saqlash
        </Button>
        {saved && <span className="cn-muted">Saqlandi</span>}
      </div>
    </Card>
  );
}

function EditModal({ open, onClose, data, onDone }: { open: boolean; onClose: () => void; data: TenantDetail; onDone: () => void }) {
  const t = data.tenant;
  const [name, setName] = useState(t.name);
  const [city, setCity] = useState(t.city ?? '');
  const [ownerName, setOwnerName] = useState(t.owner_name ?? '');
  const [grace, setGrace] = useState(String(t.grace_days));
  const act = useAction(async () => {
    await api.rpc('platform_tenant_update', {
      p_tenant: t.id,
      p_patch: { name, city, owner_name: ownerName, grace_days: Number(grace) },
    });
    onDone();
    onClose();
  });
  const graceOk = /^\d+$/.test(grace) && Number(grace) <= 60;
  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={act.busy}
      title="Ma’lumotlarni tahrirlash"
      footer={
        <>
          <Button onClick={onClose} disabled={act.busy}>
            Bekor qilish
          </Button>
          <Button variant="primary" disabled={act.busy || !name.trim() || !graceOk} onClick={() => void act.run()}>
            Saqlash
          </Button>
        </>
      }
    >
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <TextField label="Restoran nomi" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
      <TextField label="Shahar" value={city} onChange={(e) => setCity(e.target.value)} />
      <TextField label="Ega ismi" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
      <TextField
        label="Muhlat, kun"
        inputMode="numeric"
        value={grace}
        onChange={(e) => setGrace(e.target.value)}
        error={graceOk ? undefined : '0–60 kun'}
        hint="To‘lov muddati o‘tgach restoran necha kun ishlashda davom etadi"
      />
      <p className="cn-muted" style={{ margin: 0 }}>
        Telefon raqami — egasining logini. Uni almashtirish alohida amal bilan qo‘shiladi.
      </p>
    </Modal>
  );
}

function PlanModal({ open, onClose, data, onDone }: { open: boolean; onClose: () => void; data: TenantDetail; onDone: () => void }) {
  const plans = useAsync(() => (open ? api.rpc<Plan[]>('platform_plans') : Promise.resolve([] as Plan[])), [open]);
  const [planId, setPlanId] = useState(data.tenant.plan_id);
  const [reason, setReason] = useState('');
  const [disabled, setDisabled] = useState<string[] | null>(null);
  const act = useAction(async () => {
    const r = await api.rpc<{ disabled_modules: string[]; kept_modules?: string[] }>('platform_set_plan', {
      p_tenant: data.tenant.id,
      p_plan: planId,
      p_reason: reason || null,
    });
    setDisabled(r.disabled_modules);
    onDone();
    if (r.disabled_modules.length === 0) onClose();
  });
  const target = plans.data?.find((p) => p.id === planId);
  const current = plans.data?.find((p) => p.id === data.tenant.plan_id) ?? data.plan;
  const enabled = data.modules.filter((m) => m.enabled).map((m) => m.key);
  const label = (k: string) => getModule(k)?.label ?? k;
  // Server bilan bir xil qoidalar (Q-081): pullik qo'shimcha bo'lim qoladi, limit — yangi tarifniki, ishlab turganlari uzilmaydi
  const change = target ? planChangeModules(enabled, target, (k) => getModule(k)?.requires ?? []) : null;
  const kept = change ? change.kept.filter((m) => !target!.modules.includes(m)) : [];
  const used = Object.fromEntries(Object.entries(data.limits).map(([k, v]) => [k, v.used]));
  const newLimits = target ? planChangeLimits(current.limits, target.limits, data.tenant.limits ?? {}, used) : {};
  const branches = data.branches.filter((b) => b.is_active).length;
  const privileged = data.tenant.privileged === true;
  const before = quoteMonthly(current, {
    extra: extraDevices(current.limits, data.tenant.limits ?? {}),
    modules: enabled,
    branches,
    privileged,
  });
  const after = target
    ? quoteMonthly(target, { extra: extraDevices(target.limits, newLimits), modules: change!.kept, branches, privileged })
    : null;
  const tooManyBranches = target ? branches > target.max_branches : false;
  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={act.busy}
      wide
      title="Tarifni almashtirish"
      footer={
        disabled ? (
          <Button variant="primary" onClick={onClose}>
            Yopish
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={act.busy}>
              Bekor qilish
            </Button>
            <Button variant="primary" disabled={act.busy || planId === data.tenant.plan_id || tooManyBranches} onClick={() => void act.run()}>
              Almashtirish
            </Button>
          </>
        )
      }
    >
      {disabled ? (
        <Alert tone="warning" title="Tarif almashtirildi">
          Yangi tarifda yo‘q bo‘limlar o‘chirildi: {disabled.map(label).join(', ')}
        </Alert>
      ) : (
        <>
          {act.error && <Alert tone="danger">{act.error}</Alert>}
          {plans.error && <Alert tone="danger">{plans.error.message}</Alert>}
          <SelectField
            label="Tarif"
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            options={(plans.data ?? [data.plan]).filter((p) => p.is_active).map((p) => ({
              value: p.id,
              label: `${p.label} — ${limitsText(p.limits)}, ${p.max_staff} xodim`,
            }))}
          />
          {target && planId !== data.tenant.plan_id && (
            <>
              {tooManyBranches && (
                <Alert tone="danger">
                  Restoranda {branches} ta faol filial, “{target.label}” tarifida — {target.max_branches} ta. Avval filialni o‘chiring.
                </Alert>
              )}
              {change!.disabled.length > 0 && (
                <Alert tone="warning">Bu tarifda yo‘q bo‘limlar o‘chiriladi: {change!.disabled.map(label).join(', ')}</Alert>
              )}
              {kept.length > 0 && (
                <Alert tone="info">Qo‘shimcha sifatida qoladi (oylik summaga qo‘shiladi): {kept.map(label).join(', ')}</Alert>
              )}
              <div className="cn-preview--cmp">
                <div>
                  <h3 className="cn-wsec__sub">Hozir · {current.label}</h3>
                  <MonthlyPreview quote={before} planLabel={current.label} privileged={privileged} />
                </div>
                <div>
                  <h3 className="cn-wsec__sub">Keyin · {target.label}</h3>
                  <MonthlyPreview quote={after!} planLabel={target.label} privileged={privileged} />
                </div>
              </div>
              <p className="cn-muted" style={{ margin: 0 }}>
                Qurilmalar yangi tarif bo‘yicha: {limitsText({ ...target.limits, ...newLimits })}. Ishlab turgan qurilmalar uzilmaydi — tarifdan ortig‘i
                qo‘shimcha bo‘lib qoladi. “Hozir” — amaldagi narxlarda; narx kafolati bo‘lsa, u faqat hozirgi tarifda amal qiladi.
              </p>
            </>
          )}
          <TextAreaField label="Sabab (ixtiyoriy)" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </>
      )}
    </Modal>
  );
}

function toInputDate(iso: string | null): string {
  const d = iso ? new Date(iso) : new Date(Date.now() + 14 * 86_400_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function TrialModal({ open, onClose, data, onDone }: { open: boolean; onClose: () => void; data: TenantDetail; onDone: () => void }) {
  const [date, setDate] = useState(toInputDate(data.tenant.trial_ends_at));
  const act = useAction(async () => {
    // Kun oxirigacha (mahalliy vaqt bilan 23:59)
    const end = new Date(`${date}T23:59:00`);
    await api.rpc('platform_set_trial', { p_tenant: data.tenant.id, p_ends_at: end.toISOString() });
    onDone();
    onClose();
  });
  // Bepul pilot (Q-042): bugundan 6 oy — oy oxiri to'g'ri hisoblanadi (31-avgust + 6 oy = 28/29-fevral)
  const sixMonths = () => {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() + 6, 1);
    d.setDate(Math.min(now.getDate(), new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()));
    setDate(toInputDate(d.toISOString()));
  };
  const addDays = (n: number) => {
    const base = new Date(Math.max(Date.now(), data.tenant.trial_ends_at ? new Date(data.tenant.trial_ends_at).getTime() : 0));
    setDate(toInputDate(new Date(base.getTime() + n * 86_400_000).toISOString()));
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={act.busy}
      title="Sinov muddati"
      footer={
        <>
          <Button onClick={onClose} disabled={act.busy}>
            Bekor qilish
          </Button>
          <Button variant="primary" disabled={act.busy || !date} onClick={() => void act.run()}>
            Saqlash
          </Button>
        </>
      }
    >
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <TextField label="Sinov tugaydigan kun" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      <div className="cn-row">
        <Button size="sm" onClick={() => addDays(7)}>
          +7 kun
        </Button>
        <Button size="sm" onClick={() => addDays(14)}>
          +14 kun
        </Button>
        <Button size="sm" onClick={() => addDays(30)}>
          +30 kun
        </Button>
        <Button size="sm" variant="primary" onClick={sixMonths}>
          Bugundan 6 oy
        </Button>
      </div>
    </Modal>
  );
}
